import { Component, computed, OnInit, signal, TemplateRef, ViewChild } from "@angular/core";
import { FormControl, ReactiveFormsModule, UntypedFormControl } from "@angular/forms";
import { MatButtonModule } from "@angular/material/button";
import { MatDialog } from "@angular/material/dialog";
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatIconModule } from "@angular/material/icon";
import { MatInputModule } from "@angular/material/input";
import { PageEvent } from "@angular/material/paginator";
import { ActivatedRoute, Router } from "@angular/router";
import { LIST_PAGE_SIZE_OPTIONS, paginatorConfig } from "@app/app.const";
import { PrintLabelsDialogComponent } from "@components/print-labels-dialog/print-labels-dialog.component";

import { DataTableComponent } from "@components/table/data-table/data-table.component";
import {
  TableAction,
  TableColumn,
  TableConfig,
  TableSortEvent,
} from "@components/table/models/table-config.model";
import { AppStateService, RequestService } from "@services/index";
import { debounceTime } from "rxjs/operators";
import {
  FilterBarComponent,
  FormAutocompleteComponent,
  FormAutocompleteOption,
  RefreshButtonComponent,
} from "../../../components";
import { formatCountryCode } from "../../../utils/country-code.util";

@Component({
  selector: "fh-order-list",
  templateUrl: "./order-list.component.html",
  styleUrls: ["./order-list.component.css"],
  standalone: true,
  imports: [
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatIconModule,
    FilterBarComponent,
    DataTableComponent,
    FormAutocompleteComponent,
    ReactiveFormsModule,
    RefreshButtonComponent,
  ],
})
export class OrderListComponent implements OnInit {
  /** Bound wrapper for the refresh button — see `loadOrders()`. */
  refreshData = (): void => this.loadOrders();
  // Template refs for inline-edit cells (static:true → available before first render)
  @ViewChild("adminNotesTemplate", { static: true }) adminNotesTemplateRef!: TemplateRef<any>;
  @ViewChild("orderRemarksTemplate", { static: true }) orderRemarksTemplateRef!: TemplateRef<any>;

  // Inline-edit state
  editingNotesId = signal<string | null>(null);
  notesEditValue = signal<string>("");
  editingRemarksId = signal<string | null>(null);
  remarkEditValue = signal<string>("");

  // State signals
  iso2 = signal<string | null>(null);
  filterType = signal<string>("orderId");
  searchTerm = signal<string>("");
  pageIndex = signal<number>(0);
  pageSize = signal<number>(paginatorConfig.initialPageSize);
  sortProp = signal<string>("created_on");
  sortDir = signal<"asc" | "desc">("desc");
  isLoading = signal<boolean>(false);

  // Data signals
  orders = signal<any[]>([]);
  total = signal<number>(0);

  // Form controls
  searchControl = new UntypedFormControl("");

  // Computed
  destinationCountries = computed(() => this.appStateService.destinationCountries());

  countryOptions = computed<FormAutocompleteOption<string>[]>(() => {
    return this.destinationCountries().map((country) => ({
      value: country.iso2,
      label: country.name,
      count: country.itemCount,
    }));
  });

  filterOptions: FormAutocompleteOption<string>[] = [
    { label: "Order ID", value: "orderId" },
    { label: "Customer Name", value: "customerName" },
    { label: "Customer Email", value: "customerEmail" },
    { label: "Customer Remarks", value: "remarks" },
    { label: "Order Status", value: "orderStatus" },
    { label: "Admin Notes", value: "adminNotes" },
  ];

  orderStatusOptions: FormAutocompleteOption<string>[] = [
    { label: "In Progress", value: "in_progress" },
    { label: "Cancelled", value: "cancelled" },
    { label: "Confirmed", value: "confirmed" },
  ];

  countryFilterFormControl = new FormControl<string | null>(null);

  filterTypeFilterFormControl = new FormControl<string | null>(null);

  orderStatusFilterFormControl = new FormControl<string | null>(null);

  tableConfig = computed<TableConfig<any>>(() => ({
    columns: this.getTableColumns(),
    data: this.orders(),
    total: this.total(),
    loading: this.isLoading(),
    pageSizeOptions: LIST_PAGE_SIZE_OPTIONS,
    initialPageSize: paginatorConfig.initialPageSize,
    showFirstLastButtons: true,
    stickyHeader: true,
    highlightRowOnClick: false,
    rowClickable: false,
    detailsRoute: "/order_details/:id",
    getDetailsRouteParams: (row) => ({ id: row.id }),
  }));

  // The view-details action is now rendered automatically by fh-data-table from
  // config().detailsRoute (a real routerLink, so right-click "open in new tab"
  // works) — this used to duplicate it with a (click)-handler button that couldn't
  // support that.
  // Shipping labels: orders in any live market (admin-backend shipping-label.ts).
  tableActions = computed<TableAction<any>[]>(() => [
    {
      icon: "print",
      label: "Print shipping labels",
      handler: (row) => this.printLabels([String(row.id)]),
      show: (row) =>
        this.appStateService.liveMarketCodes().includes(String(row.iso2 ?? "").toUpperCase()),
    },
  ]);
  /** The orders on this page in a live market, for "Print labels". */
  pageSgOrderIds = computed(() =>
    this.orders()
      .filter((o: any) =>
        this.appStateService.liveMarketCodes().includes(String(o.iso2 ?? "").toUpperCase())
      )
      .map((o: any) => String(o.id))
  );

  readonly paginatorConfig = paginatorConfig;

  // ── Admin Notes inline edit ───────────────────────────────────────────────
  startEditNotes(row: any, event: Event): void {
    event.stopPropagation();
    this.editingNotesId.set(row.id);
    this.notesEditValue.set(row.admin_notes ?? "");
    setTimeout(() => {
      (document.getElementById(`notes-${row.id}`) as HTMLInputElement)?.focus();
    }, 0);
  }

  saveAdminNotes(row: any): void {
    if (this.editingNotesId() !== row.id) return;
    const newValue = this.notesEditValue();
    const oldValue = row.admin_notes ?? "";
    this.editingNotesId.set(null);
    if (newValue === oldValue) return;
    this.auth
      .post("/api/v1/update_admin_notes_for_order", { adminNotes: newValue, orderId: row.id })
      .subscribe({
        next: (res: any) => {
          if (res.result_code === 1) {
            this.orders.update((list) =>
              list.map((o) => (o.id === row.id ? { ...o, admin_notes: newValue } : o))
            );
          }
        },
        error: (err) => console.error("Failed to update admin notes:", err),
      });
  }

  cancelNotesEdit(): void {
    this.editingNotesId.set(null);
  }

  // ── Remarks inline edit ───────────────────────────────────────────────────
  startEditRemark(row: any, event: Event): void {
    event.stopPropagation();
    this.editingRemarksId.set(row.id);
    this.remarkEditValue.set(row.remarks ?? "");
    setTimeout(() => {
      (document.getElementById(`remark-${row.id}`) as HTMLInputElement)?.focus();
    }, 0);
  }

  saveOrderRemarks(row: any): void {
    if (this.editingRemarksId() !== row.id) return;
    const newValue = this.remarkEditValue();
    const oldValue = row.remarks ?? "";
    this.editingRemarksId.set(null);
    if (newValue === oldValue) return;
    this.auth
      .post("/api/v1/update_remarks_for_order", { orderId: row.id, remarks: newValue })
      .subscribe({
        next: (res: any) => {
          if (res.result_code === 1) {
            this.orders.update((list) =>
              list.map((o) => (o.id === row.id ? { ...o, remarks: newValue } : o))
            );
          }
        },
        error: (err) => console.error("Failed to update remarks:", err),
      });
  }

  cancelRemarkEdit(): void {
    this.editingRemarksId.set(null);
  }

  constructor(
    private auth: RequestService,
    private appStateService: AppStateService,
    private router: Router,
    private route: ActivatedRoute,
    private dialog: MatDialog
  ) {}

  printLabels(orderIds: string[]): void {
    if (!orderIds.length) return;
    this.dialog.open(PrintLabelsDialogComponent, { data: { orderIds }, width: "420px" });
  }

  ngOnInit(): void {
    this.setupSearchControl();
    this.route.queryParams.subscribe((params) => {
      this.pageIndex.set(+(params.page || 0));
      this.pageSize.set(+(params.pageSize || paginatorConfig.initialPageSize));
      this.iso2.set(params.iso2 || null);
      this.filterType.set(params.filterType || "orderId");
      this.searchTerm.set(params.searchTerm || "");
      this.sortProp.set(params.sortProp || "created_on");
      this.sortDir.set((params.sortDir || "desc") as "asc" | "desc");

      // for the first time when this page loads we need to patch value to form control
      this.countryFilterFormControl.patchValue(this.iso2() || null);
      this.filterTypeFilterFormControl.patchValue(this.filterType() || null);

      this.searchControl.setValue(this.searchTerm(), { emitEvent: false });
      this.loadOrders();
    });
  }

  onCountryChange(iso2: string | null): void {
    this.updateQueryParams({ iso2: iso2 || undefined, page: 0 });
  }

  onFilterTypeChange(filterType: string | number | boolean | null): void {
    this.updateQueryParams({
      filterType: filterType ? String(filterType) : "orderId",
      searchTerm: undefined,
      page: 0,
    });
  }

  onOrderStatusChange(status: string | number | boolean | null): void {
    this.searchTerm.set(status ? String(status) : "");
    this.onSearch();
  }

  onSearch(): void {
    this.updateQueryParams({ searchTerm: this.searchTerm() || undefined, page: 0 });
  }

  onResetFilters(): void {
    this.searchControl.setValue("");
    this.updateQueryParams({
      iso2: undefined,
      filterType: "orderId",
      searchTerm: undefined,
      page: 0,
    });
  }

  onSortChange(event: TableSortEvent): void {
    this.updateQueryParams({
      sortProp: event.prop || undefined,
      sortDir: event.dir || undefined,
      page: 0,
    });
  }

  onPageChange(event: PageEvent): void {
    this.updateQueryParams({
      page: event.pageIndex,
      pageSize: event.pageSize,
    });
  }

  resetFilters(): void {
    this.onResetFilters();
  }

  private setupSearchControl(): void {
    this.searchControl.valueChanges.pipe(debounceTime(300)).subscribe((value) => {
      this.searchTerm.set(value || "");
    });
  }

  private loadOrders(): void {
    let apiUrl = `/api/v2/orders?pageIndex=${this.pageIndex()}&pageSize=${this.pageSize()}`;

    // Backend expects `orderField` + `order`, not `sortProp` + `sortDir`
    if (this.sortProp() && this.sortDir()) {
      apiUrl += `&orderField=${this.sortProp()}&order=${this.sortDir()}`;
    }

    if (this.iso2()) {
      apiUrl += `&iso2=${this.iso2()}`;
    }

    const filterType = this.filterType();
    const searchTerm = this.searchTerm();

    if (searchTerm) {
      if (filterType === "customerName") {
        apiUrl += `&customerName=${encodeURIComponent(searchTerm)}`;
      } else if (filterType === "customerEmail") {
        apiUrl += `&customerEmail=${encodeURIComponent(searchTerm)}`;
      } else if (filterType === "remarks") {
        apiUrl += `&remarks=${encodeURIComponent(searchTerm)}`;
      } else if (filterType === "orderStatus") {
        apiUrl += `&orderStatus=${encodeURIComponent(searchTerm)}`;
      } else if (filterType === "adminNotes") {
        apiUrl += `&adminNotes=${encodeURIComponent(searchTerm)}`;
      } else if (filterType === "orderId") {
        apiUrl += `&orderId=${encodeURIComponent(searchTerm)}`;
      }
    }

    this.isLoading.set(true);
    this.auth.get(apiUrl).subscribe({
      next: (response: any) => {
        if (response.result_code === 1) {
          this.orders.set(response.data.list || []);
          // Backend sends `totalRecords`, not `count`
          this.total.set(response.data.totalRecords || 0);
        }
        this.isLoading.set(false);
      },
      error: () => {
        this.orders.set([]);
        this.total.set(0);
        this.isLoading.set(false);
      },
    });
  }

  private updateQueryParams(params: any): void {
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: params,
      queryParamsHandling: "merge",
    });
  }

  private getTableColumns(): TableColumn<any>[] {
    return [
      {
        key: "id",
        header: "Order ID",
        sortable: true,
        width: "150px",
      },
      {
        key: "customer_name",
        header: "Customer",
        sortable: true,
        width: "180px",
      },
      {
        key: "iso2",
        header: "Country",
        sortable: true,
        width: "100px",
        format: formatCountryCode,
      },
      {
        key: "currency",
        header: "Currency",
        sortable: true,
        width: "100px",
      },
      {
        key: "sub_total",
        header: "Subtotal",
        sortable: true,
        type: "number",
        width: "120px",
        format: (value) => `$${Number(value || 0).toFixed(2)}`,
      },
      {
        key: "shipping_total",
        header: "Shipping",
        sortable: true,
        type: "number",
        width: "120px",
        format: (value) => `$${Number(value || 0).toFixed(2)}`,
      },
      {
        key: "subtotalDiscount",
        header: "Subtotal Discount",
        sortable: false,
        type: "number",
        format: (value) => `$${Number(value || 0).toFixed(2)}`,
      },
      {
        key: "deliveryDiscount",
        header: "Delivery Discount",
        sortable: false,
        type: "number",
        format: (value) => `$${Number(value || 0).toFixed(2)}`,
      },
      {
        key: "subtotalDeliveryDiscount",
        header: "Combined Discount",
        sortable: false,
        type: "number",
        format: (value) => `$${Number(value || 0).toFixed(2)}`,
      },
      {
        key: "total",
        header: "Total",
        sortable: true,
        type: "number",
        width: "120px",
        format: (value) => `$${Number(value || 0).toFixed(2)}`,
      },
      {
        key: "created_on",
        header: "Created",
        sortable: true,
        type: "date",
        width: "150px",
        format: (value) => new Date(value).toLocaleString("en-US"),
      },
      {
        key: "payment_type",
        header: "Payment Type",
        sortable: true,
        format: (value) => {
          const labels: Record<string, string> = {
            card: "Credit Card",
            apple_pay: "Apple Pay",
            google_pay: "Google Pay",
            paynow: "PayNow",
            grabpay: "GrabPay",
            link: "Link",
          };
          return labels[value] || (value ? value : "—");
        },
      },
      {
        key: "order_status",
        header: "Order Status",
        sortable: true,
        width: "140px",
        format: (value) => {
          const labels: Record<string, string> = {
            in_progress: "In Progress",
            confirmed: "Confirmed",
            cancelled: "Cancelled",
          };
          return labels[value] ?? (value ? value.charAt(0).toUpperCase() + value.slice(1) : "—");
        },
        cssClass: (row) => `orderStatus-${row.order_status}`,
      },
      {
        key: "remarks",
        header: "Customer Remarks",
        sortable: false,
        width: "200px",
        template: this.orderRemarksTemplateRef,
      },
      {
        key: "admin_notes",
        header: "Admin Notes",
        sortable: false,
        width: "200px",
        template: this.adminNotesTemplateRef,
      },
    ];
  }
}
